import React from 'react'
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Stepper, Step, StepLabel, Box, Typography, TextField, Stack, IconButton } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { Trans, t } from '@lingui/macro'
import { FormTemplate, AIOutputConfig, PdfFieldDto } from '@/core/models/form.model'
import UploadButton from '@/components/ui/buttons/UploadButton'
import DeleteIcon from '@mui/icons-material/Delete'
import { PdfFieldEditor } from './PdfFieldEditor'
import FilePreview from '@/components/ui/preview/FilePreview'

interface PdfWorkflowDialogProps {
    open: boolean
    onClose: () => void
    template: FormTemplate
    onUploadPdf: (file: File, templateId: string) => Promise<any>
    onDeletePdf: (file: any, templateId: string) => Promise<void>
    onDetectFields: (templateId: string) => Promise<PdfFieldDto[]>
    onBurnPdf: (templateId: string) => Promise<any>
    onSaveTemplate: (updates: Partial<FormTemplate>) => Promise<void>
}

const steps = [t`Upload PDF`, t`Detect Fields`, t`Review & Edit`, t`Finalize`]

export const PdfWorkflowDialog: React.FC<PdfWorkflowDialogProps> = ({
    open,
    onClose,
    template,
    onUploadPdf,
    onDeletePdf,
    onDetectFields,
    onBurnPdf,
    onSaveTemplate
}) => {
    const [activeStep, setActiveStep] = React.useState(0)
    const [aiOutputConfig, setAiOutputConfig] = React.useState<AIOutputConfig>(template.aiOutputConfig || { enabled: true })
    const [pdfFields, setPdfFields] = React.useState<PdfFieldDto[]>(template.pdfFields || [])
    const [fillablePdf, setFillablePdf] = React.useState<any>(template.fillablePdf)
    const [isLoading, setIsLoading] = React.useState(false)

    // Sync state with template when it changes
    React.useEffect(() => {
        if (open) {
            setAiOutputConfig(template.aiOutputConfig || { enabled: true })
            setPdfFields(template.pdfFields || [])
            setFillablePdf(template.fillablePdf)

            // Determine initial step based on state
            if (!template.fillablePdf) {
                setActiveStep(0)
            } else if (!template.pdfFields || template.pdfFields.length === 0) {
                setActiveStep(1)
            } else {
                setActiveStep(2)
            }
        }
    }, [template, open])

    const handleUpload = async (file: File) => {
        setIsLoading(true)
        try {
            const result = await onUploadPdf(file, template._id)
            setFillablePdf(result)
            // Auto advance
            setActiveStep(1)
        } catch (error) {
            console.error(error)
        } finally {
            setIsLoading(false)
        }
    }

    const handleDelete = async () => {
        if (!fillablePdf) return
        setIsLoading(true)
        try {
            await onDeletePdf(fillablePdf, template._id)
            setFillablePdf(undefined)
            setPdfFields([])
            setActiveStep(0)
        } catch (error) {
            console.error(error)
        } finally {
            setIsLoading(false)
        }
    }

    const handleDetect = async () => {
        setIsLoading(true)
        try {
            // First save config updates (prompt, filename template)
            await onSaveTemplate({ aiOutputConfig })

            const fields = await onDetectFields(template._id)
            setPdfFields(fields)
            setActiveStep(2)
        } catch (error) {
            console.error(error)
        } finally {
            setIsLoading(false)
        }
    }

    const handleSaveFields = async () => {
        setIsLoading(true)
        try {
            await onSaveTemplate({ pdfFields, aiOutputConfig })
            setActiveStep(3)
        } catch (error) {
            console.error(error)
        } finally {
            setIsLoading(false)
        }
    }

    const handleBurn = async () => {
        setIsLoading(true)
        try {
            await onBurnPdf(template._id)
            onClose()
        } catch (error) {
            console.error(error)
        } finally {
            setIsLoading(false)
        }
    }

    const renderStepContent = (step: number) => {
        switch (step) {
            case 0:
                return (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography variant="h6" gutterBottom><Trans>Upload PDF Template</Trans></Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                            <Trans>Upload the PDF form you want to make fillable.</Trans>
                        </Typography>

                        {fillablePdf ? (
                            <Stack direction="row" alignItems="center" justifyContent="center" spacing={2} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                <Typography>{fillablePdf.filename}</Typography>
                                <IconButton onClick={handleDelete} color="error">
                                    <DeleteIcon />
                                </IconButton>
                            </Stack>
                        ) : (
                            <UploadButton onUpload={handleUpload} />
                        )}
                    </Box>
                )
            case 1:
                return (
                    <Box sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom><Trans>Configure AI Detection</Trans></Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            <Trans>Configure the AI settings before detecting fields in the PDF.</Trans>
                        </Typography>
                        <Stack spacing={3}>
                            <TextField
                                fullWidth
                                label={t`Output Filename Template`}
                                value={aiOutputConfig.outputFilenameTemplate || ''}
                                onChange={(e) => setAiOutputConfig({ ...aiOutputConfig, outputFilenameTemplate: e.target.value })}
                                helperText={<Trans>Use placeholders like {'{patientName}'}, {'{date}'} to customize the filename.</Trans>}
                                size="small"
                            />
                            <TextField
                                fullWidth
                                label={t`AI Prompt / Instructions`}
                                value={aiOutputConfig.prompt || ''}
                                onChange={(e) => setAiOutputConfig({ ...aiOutputConfig, prompt: e.target.value })}
                                multiline
                                minRows={8}
                                maxRows={20}
                                helperText={<Trans>Provide detailed instructions to the AI on how to map form responses to PDF fields.</Trans>}
                                InputProps={{
                                    sx: { fontFamily: 'monospace', fontSize: '0.9rem' }
                                }}
                            />
                        </Stack>
                    </Box>
                )
            case 2:
                return (
                    <Box sx={{ height: '60vh', display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="h6" sx={{ p: 2 }}><Trans>Review & Edit Fields</Trans></Typography>
                        <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                            <PdfFieldEditor
                                fileUrl={fillablePdf?.url || ''}
                                fields={pdfFields}
                                onChange={setPdfFields}
                            />
                        </Box>
                    </Box>
                )
            case 3:
                return (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography variant="h5" gutterBottom><Trans>Ready to Finalize</Trans></Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            <Trans>You have configured <strong>{pdfFields.length}</strong> fields.</Trans>
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                            <Trans>Click "Burn PDF" to generate the final fillable PDF and save it to the template.</Trans>
                        </Typography>

                        {template.fillablePdf && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="body2" color="success.main" sx={{ mb: 1 }}>
                                    <Trans>PDF has been successfully burned!</Trans>
                                </Typography>
                                <Button
                                    variant="outlined"
                                    onClick={() => setPreviewFile(template.fillablePdf)}
                                >
                                    <Trans>View Burned PDF</Trans>
                                </Button>
                            </Box>
                        )}
                    </Box>
                )
            default:
                return null
        }
    }

    const [previewFile, setPreviewFile] = React.useState<any>(null)

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
                <DialogTitle>
                    <Stepper activeStep={activeStep}>
                        {steps.map((label) => (
                            <Step key={label}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                </DialogTitle>
                <DialogContent>
                    {renderStepContent(activeStep)}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}><Trans>Close</Trans></Button>
                    <Box sx={{ flexGrow: 1 }} />

                    {activeStep > 0 && (
                        <Button onClick={() => setActiveStep(prev => prev - 1)} disabled={isLoading}>
                            <Trans>Back</Trans>
                        </Button>
                    )}

                    {activeStep === 0 && (
                        <Button
                            variant="contained"
                            onClick={() => setActiveStep(1)}
                            disabled={!fillablePdf || isLoading}
                        >
                            <Trans>Next</Trans>
                        </Button>
                    )}

                    {activeStep === 1 && (
                        <LoadingButton
                            variant="contained"
                            onClick={handleDetect}
                            loading={isLoading}
                        >
                            <Trans>Detect Fields</Trans>
                        </LoadingButton>
                    )}

                    {activeStep === 2 && (
                        <LoadingButton
                            variant="contained"
                            onClick={handleSaveFields}
                            loading={isLoading}
                        >
                            <Trans>Save & Continue</Trans>
                        </LoadingButton>
                    )}

                    {activeStep === 3 && (
                        <LoadingButton
                            variant="contained"
                            color="primary"
                            onClick={handleBurn}
                            loading={isLoading}
                        >
                            {template.fillablePdf ? <Trans>Re-Burn PDF</Trans> : <Trans>Burn PDF</Trans>}
                        </LoadingButton>
                    )}
                </DialogActions>
            </Dialog>

            {previewFile && (
                <Dialog open={!!previewFile} onClose={() => setPreviewFile(null)} maxWidth="lg" fullWidth>
                    <DialogTitle>
                        {previewFile.filename}
                        <IconButton
                            aria-label="close"
                            onClick={() => setPreviewFile(null)}
                            sx={{
                                position: 'absolute',
                                right: 8,
                                top: 8,
                                color: (theme) => theme.palette.grey[500],
                            }}
                        >
                            <Box component="span" sx={{ fontSize: 20 }}>×</Box>
                        </IconButton>
                    </DialogTitle>
                    <DialogContent sx={{ height: '80vh', p: 0 }}>
                        <FilePreview
                            fileName={previewFile.filename}
                            fileType="pdf"
                            filePath={previewFile.url}
                        />
                    </DialogContent>
                </Dialog>
            )}
        </>
    )
}
